import { X, Info, Sparkle, Envelope, ArrowSquareOut } from "@phosphor-icons/react";
import { useSystemAbout } from "@/lib/useSystemAbout";

/**
 * AboutDialog — About Coolman Operations.
 *
 * Version 1.0.1 · Product Owner directive 2026-07-30.
 * Content is populated exclusively from the authoritative
 * /api/system/about endpoint — the frontend never hardcodes version,
 * channel or environment.
 */
export default function AboutDialog({ open, onClose }) {
  const about = useSystemAbout();
  if (!open) return null;

  const info = about || {
    product: "Coolman Operations",
    company: "Coolman Fuels",
    version: "…",
    channel: "GA",
    environment: "…",
    release_date: "…",
    build: "…",
    copyright: "",
    support_email: "",
    release_notes: "",
  };

  const envTone =
    info.environment === "Production"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : info.environment === "Preview"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div
      data-testid="about-dialog-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-inverse/50 px-4"
      onClick={onClose}
    >
      <div
        data-testid="about-dialog"
        className="w-full max-w-md overflow-hidden rounded-lg border border-divider bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-divider bg-surface-alt px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-brand/10 p-2 text-brand">
              <Sparkle size={20} weight="fill" />
            </div>
            <div>
              <div
                data-testid="about-dialog-product"
                className="font-display text-lg font-semibold text-ink-primary"
              >
                About {info.product}
              </div>
              <div className="text-xs text-ink-secondary">
                {info.company} · Internal Operations Platform
              </div>
            </div>
          </div>
          <button
            data-testid="about-dialog-close"
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-secondary hover:bg-surface"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <dl className="divide-y divide-divider text-sm">
          <Row label="Version" value={
            <span className="font-mono" data-testid="about-dialog-version">
              {info.version} <span className="text-ink-secondary">· {info.channel}</span>
            </span>
          }/>
          <Row label="Environment" value={
            <span
              data-testid="about-dialog-environment"
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${envTone}`}
            >
              {info.environment}
            </span>
          }/>
          {info.outbound_communications && (
            <Row label="Outbound communications" value={
              <span
                data-testid="about-dialog-outbound-communications"
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                  info.outbound_communications === "ENABLED"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
              >
                {info.outbound_communications}
              </span>
            }/>
          )}
          <Row label="Release date" value={
            <span data-testid="about-dialog-release-date">{info.release_date}</span>
          }/>
          <Row label="Build" value={
            <span className="font-mono text-[12px] text-ink-secondary" data-testid="about-dialog-build">
              {info.build}
            </span>
          }/>
        </dl>

        <div className="border-t border-divider bg-surface-alt px-5 py-3">
          <div className="flex flex-col gap-2 text-xs text-ink-secondary sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1.5">
              <Info size={12} weight="bold" />
              <span data-testid="about-dialog-copyright">{info.copyright}</span>
            </div>
            <div className="flex items-center gap-3">
              {info.support_email && (
                <a
                  data-testid="about-dialog-support"
                  href={`mailto:${info.support_email}`}
                  className="inline-flex items-center gap-1 text-brand hover:text-brand-hover"
                >
                  <Envelope size={12} weight="bold" /> {info.support_email}
                </a>
              )}
              {info.release_notes && (
                <a
                  data-testid="about-dialog-release-notes"
                  href={info.release_notes}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand hover:text-brand-hover"
                >
                  Release notes <ArrowSquareOut size={12} weight="bold" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className="text-ink-secondary">{label}</dt>
      <dd className="text-right text-ink-primary">{value}</dd>
    </div>
  );
}
